import { useCallback, useRef } from 'react'
import i18next, { type TFunction } from 'i18next'
import { showUndoToast } from '@/src/renderer/components/common/UndoToast'
import { useDataSource } from '../../../lib/dataSource'
import type { Task, TaskCascade } from '../../../lib/db/models'
import type { TaskCreationData } from '../components/TaskEntry'
import { useSoftRemoveSound } from '@/src/renderer/lib/sounds'
import { requestDoneMoment, type DoneMomentOrigin } from '@/src/renderer/lib/doneMoment'
import { retractSpawnedSuccessor, spawnNextOccurrence } from '../utils/spawnNextOccurrence'
import { cascadeDescription } from '../utils/cascadeDescription'
import { dismissComebackToast, showComebackToast } from '../utils/comebackToast'
import { rememberUndo } from '@/src/renderer/lib/undo'

interface UseTaskOperationsParams {
  t: TFunction
  /** Optional optimistic-update sink. When provided, every mutation
   *  here calls it BEFORE the IDB write so the change is visible in
   *  the next paint instead of waiting on the LiveQuery refire. */
  setOptimisticTaskOverride?: (taskId: string, partial: Partial<Task>) => void
  /** Optional in-memory `tasks` snapshot used by toggle-complete to
   *  read the current `status` synchronously (for the optimistic flip)
   *  without an extra `getTask` roundtrip. */
  tasks?: Task[]
  /**
   * Woher der Haken kommt — entscheidet über den **Geschafft-Moment**
   * (`lib/doneMoment`). Aus einer Liste erscheint er nur mit Belohnung, aus dem
   * **Fokus** immer; die Pille setzt darum `'focus'`.
   *
   * Als Option und nicht als zweiter Aufruf in der Pille: Sonst liefen beim
   * Haken in der Pille BEIDE Wege, und eine Aufgabe mit Belohnung bekäme den
   * Moment doppelt.
   */
  momentOrigin?: DoneMomentOrigin
}

export function useTaskOperations({ t, setOptimisticTaskOverride, tasks, momentOrigin = 'list' }: UseTaskOperationsParams) {
  const playSoftRemove = useSoftRemoveSound()
  const source = useDataSource()

  /*
    **Der Schnappschuss wird über den Ref gelesen** (gemessen 21.09.2026 mit
    `render-audit`): `tasks` ist bei jedem Datenwechsel ein neues Array. Stand
    es in den Abhängigkeiten, wechselte `handleToggleComplete` mit — und mit
    ihm der Griff, den JEDE Zeile der Liste bekommt (`onToggleComplete×34`).
    Gebraucht wird er erst beim Abhaken; dann steht im Ref das Aktuelle.
  */
  const tasksRef = useRef(tasks)
  tasksRef.current = tasks

  const handleUpdateTask = useCallback(async (taskId: string, data: TaskCreationData) => {
    const updates = {
      title: data.title,
      description: data.description,
      ...(data.attention !== undefined && { attention: data.attention }),
      ...(data.estimatedDuration !== undefined && { estimatedDuration: data.estimatedDuration }),
      ...(data.durationMode !== undefined && { durationMode: data.durationMode }),
      ...(data.tags !== undefined && { tags: data.tags }),
      ...(data.rewardId !== undefined && { rewardId: data.rewardId }),
      ...(data.startDate !== undefined && { startDate: data.startDate }),
      ...(data.startTime !== undefined && { startTime: data.startTime }),
      ...(data.startPeriod !== undefined && { startPeriod: data.startPeriod }),
      ...(data.dueDate !== undefined && { dueDate: data.dueDate }),
      ...(data.dueTime !== undefined && { dueTime: data.dueTime }),
      ...(data.duePeriod !== undefined && { duePeriod: data.duePeriod }),
    }
    setOptimisticTaskOverride?.(taskId, updates)
    await source.updateTask(taskId, updates)
  }, [setOptimisticTaskOverride, source])

  /*
    **Der Haken ist sein eigenes Gegenteil** — derselbe Aufruf nimmt ihn
    zurück, samt Nachfolger bei Wiederkehrendem. Für `⌘Z` (`lib/undo`) meldet
    er sich deshalb selbst an, über einen Ref: Ein `useCallback` kann sich
    nicht selbst in seinen Abhängigkeiten nennen.
  */
  const toggleRef = useRef<((ids: Set<string>) => Promise<void>) | null>(null)
  const sourceRef = useRef(source)
  sourceRef.current = source

  const handleToggleComplete = useCallback(async (ids: Set<string>) => {
    // Decide the target state from the in-memory snapshot (which already
    // reflects any pending optimistic override). Without this, a second
    // rapid click reads the still-stale stored row via `getTask`
    // and flips back into the same direction as the first click — the
    // UI then visibly snaps "no change". The optimistic flip and the
    // DB write MUST use the same source of truth.
    const now = new Date()
    const idArray = [...ids]
    const successors: Task[] = []
    const completedRecurring: string[] = []
    const toggled = new Set<string>()
    // Welche in DIESEM Zug erledigt wurden (der Rest bekam den Haken heraus) —
    // der Zielzustand für `⌘Z` und die Wahl des Satzes hängen daran.
    const completedIds = new Set<string>()
    let reopenedTitle: string | null = null
    const snapshotTasks = tasksRef.current
    const byId = snapshotTasks ? new Map(snapshotTasks.map(t => [t.id, t])) : null
    for (const taskId of idArray) {
      let snapshot = byId?.get(taskId)
      if (!snapshot) {
        // Snapshot missing (rare — e.g. task only in storage, not in
        // page tasks). Fall back to the stored row.
        const fromDb = await source.getTask(taskId)
        if (!fromDb) continue
        snapshot = fromDb
      }
      toggled.add(taskId)
      const isCompleted = snapshot.status === 'completed'
      if (isCompleted) reopenedTitle ??= snapshot.title
      else completedIds.add(taskId)
      const next = isCompleted
        ? { status: 'todo' as const, completedAt: null }
        : { status: 'completed' as const, completedAt: now }
      setOptimisticTaskOverride?.(taskId, next)
      await source.updateTask(taskId, next)
      // Die Wiederkehr (ADR 0021): Abhaken legt den Nachfolger an, der Haken zurück
      // in derselben Sitzung nimmt ihn wieder weg.
      if (isCompleted) {
        await retractSpawnedSuccessor(source, taskId)
        dismissComebackToast(taskId)
      } else {
        if (snapshot.recurrence) {
          const successor = await spawnNextOccurrence(source, { ...snapshot, ...next }, now, snapshotTasks)
          if (successor) {
            successors.push(successor)
            completedRecurring.push(taskId)
          }
        }
        /*
          **Der Geschafft-Moment** (Kernschleife 3c). Nur hier, im Zweig des
          Erledigens — der Haken zurück feiert nichts. Ob er wirklich kommt,
          entscheidet `requestDoneMoment`: genau eine Aufgabe, aus dem Fokus
          immer, aus der Liste nur mit Belohnung.

          Bei Wiederkehrendem gilt er der ABGEHAKTEN Aufgabe, nicht dem
          Nachfolger — `snapshot` ist die abgehakte.
        */
        requestDoneMoment(snapshot, momentOrigin, ids.size)
      }
    }

    // **„Kommt wieder"** — sonst stünde stumm eine zweite, gleichnamige Zeile da, und das läse
    // sich wie ein Duplikat. Ein Toast für alle, die auf einmal abgehakt wurden.
    showComebackToast(successors, completedRecurring, t, i18next.language ?? 'de', now)

    /*
      **`⌘Z` nimmt den Haken zurück, solange die Liegezeit läuft** (F-Q1).
      Das Abhaken zeigt keine Rückgängig-Meldung — angeboten wird es über die
      Liegezeit, in der die Zeile an ihrem Platz stehen bleibt und ein zweiter
      Klick genügt. Das Kürzel gilt genau solange, damit es nichts zurücknimmt,
      was der Nutzer längst für erledigt hält.

      `toggled` statt `ids`: Was gar nicht da war, wird auch nicht
      zurückgenommen — sonst stünde ein Angebot, das ins Leere greift.
    */
    if (toggled.size === 0) return

    /*
      **`⌘Z` nimmt den Haken zurück** (F-Q1, ADR 0028) — ohne Frist, wie jede
      gemerkte Handlung. Das Abhaken zeigt keine Rückgängig-Meldung; angeboten
      wird es über die **Liegezeit**, und die bleibt der Mausweg.

      **Gemerkt wird der Zielzustand, nicht die Richtung.** Der bloße Aufruf
      „nochmal umschalten“ war richtig, solange er nur zwei Sekunden galt. Ohne
      Frist wäre er es nicht mehr: Wer den Haken später von Hand herausnimmt und
      dann `⌘Z` drückt, bekäme die Aufgabe WIEDER abgehakt — ein
      Wiederherstellen, kein Zurücknehmen.

      Ein zweites Fenster und der Sync gehen am Wächter von `DataSource` vorbei;
      nur sie können den Zustand ändern, ohne dass die Handlung vergessen wird.
      Darum wird vor dem Schreiben nachgesehen — und ausgelassen, was schon so
      aussieht, wie `⌘Z` es herstellen würde.

      Der Aufruf selbst bleibt `handleToggleComplete`: Am Haken hängt mehr als
      ein Feld (der **Nachfolger** bei Wiederkehrendem, der „Kommt wieder“-Hinweis).
      Den Zustand direkt zu schreiben ließe das liegen.
    */
    const expected = new Map([...toggled].map(id => [id, completedIds.has(id)] as const))
    const undoneMessage = completedIds.size > 0
      ? (completedIds.size === 1
          ? t('undo.restored.completed')
          : t('undo.restored.completedMultiple', { count: completedIds.size }))
      : t('undo.restored.reopened', { title: reopenedTitle ?? '' })

    rememberUndo(async () => {
      const stillThere: string[] = []
      for (const [id, wasCompleted] of expected) {
        const current = await sourceRef.current.getTask(id)
        if (!current) continue
        if ((current.status === 'completed') === wasCompleted) stillThere.push(id)
      }
      if (stillThere.length === 0) return false
      await toggleRef.current?.(new Set(stillThere))
      return true
    }, undoneMessage)
  }, [setOptimisticTaskOverride, t, source, momentOrigin])

  toggleRef.current = handleToggleComplete

  const handleDeleteTask = useCallback(async (ids: Set<string>) => {
    // Capture full records for undo (records may be hard-deleted by sync purge)
    const results = await Promise.all([...ids].map(id => source.getTask(id)))
    const snapshots = results.filter((s): s is NonNullable<typeof s> => s != null)
    if (snapshots.length === 0) return

    snapshots.forEach((_, i) => setTimeout(() => playSoftRemove(), i * 80))

    // Optimistic delete: mark `deletedAt` so segment builders drop the
    // task in the next paint. The actual write follows.
    const deletedAt = new Date()
    if (setOptimisticTaskOverride) {
      for (const snap of snapshots) setOptimisticTaskOverride(snap.id, { deletedAt })
    }

    // Each delete cascades to that task's subtasks AND to the Canvas-Elemente
    // that pointed at it, and reports which ones it took — undo needs those
    // lists to leave rows the user had already deleted (or unlinked) by hand
    // alone.
    const cascaded = new Map<string, TaskCascade>()
    for (const id of ids) cascaded.set(id, await source.deleteTask(id))

    const message = snapshots.length === 1
      ? t('taskItem.deleted', { title: snapshots[0]!.title })
      : t('taskItem.deletedMultiple', { count: snapshots.length })

    showUndoToast({
      message,
      // Was mitging (#223) — nur bei einer Aufgabe; bei mehreren sagt der Titel schon die Zahl.
      description: snapshots.length === 1
        ? cascadeDescription(cascaded.get(snapshots[0]!.id) ?? { subtaskIds: [], unlinkedNoteIds: [], timeEntryIds: [], commentIds: [] }, t, i18next.language ?? 'de')
        : undefined,
      undoLabel: t('common.undo'),
      undoneMessage: snapshots.length === 1
        ? t('undo.restored.task', { title: snapshots[0]!.title })
        : t('undo.restored.taskMultiple', { count: snapshots.length }),
      onUndo: async () => {
        // Optimistic restore: clear `deletedAt` immediately so the row
        // reappears without waiting on the LiveQuery refire.
        if (setOptimisticTaskOverride) {
          for (const snap of snapshots) setOptimisticTaskOverride(snap.id, { deletedAt: null })
        }
        for (const snap of snapshots) {
          // With the cascade: otherwise the task comes back as an empty shell —
          // it soft-deleted its whole checklist a moment ago. Und das
          // **Eselsohr** kommt mit zurück: Die Verbindung ist mit der Aufgabe
          // gegangen, also kehrt sie mit ihr wieder.
          await source.restoreTask(snap, cascaded.get(snap.id) ?? { subtaskIds: [], unlinkedNoteIds: [], timeEntryIds: [], commentIds: [] })
        }
      },
    })

  }, [t, playSoftRemove, setOptimisticTaskOverride, source])

  return { handleUpdateTask, handleToggleComplete, handleDeleteTask }
}
